import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket$: WebSocketSubject<any> | null = null;
  private messagesSubject = new Subject<any>();

  // ✅ Manually open a WebSocket connection
  connect(): void {
    // recreate subject every time a new connection starts
    this.messagesSubject = new Subject<any>();

    if (this.socket$ && !this.socket$.closed) {
      console.info('[WebSocket] Already connected.');
      return;
    }

    this.socket$ = webSocket({
      url: environment.WEBSOCKET_URI,
      openObserver: {
        next: () => console.info('[WebSocket] Connection opened'),
      },
      closeObserver: {
        next: () => {
          console.info('[WebSocket] Connection closed');
          this.messagesSubject.complete(); // ensure complete on natural close
        },
      },
    });

    this.socket$.subscribe({
      next: (msg) => this.messagesSubject.next(msg),
      error: (err) => {
        console.info('[WebSocket] Error:', err);
        this.messagesSubject.error(err);
      },
      complete: () => {
        console.info('[WebSocket] Completed');
        this.messagesSubject.complete();
      },
    });
  }

  // ✅ Send a message
  sendMessage(message: any): void {
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.next(message);
    } else {
      console.info('[WebSocket] Not connected. Message not sent.');
    }
  }

  // ✅ Get incoming messages
  receiveMessages(): Observable<any> {
    return this.messagesSubject.asObservable();
  }

  // ✅ Check connection status
  isSocketConnected(): boolean {
    return this.socket$ != null && !this.socket$.closed; // NOSONAR
  }

  // ✅ Close the WebSocket connection
  closeConnection(): void {
    if (this.socket$) {
      console.info('[WebSocket] Closing...');
      this.socket$.complete();
      this.socket$ = null;
    }
  }

  // ✅ Wait until connected (for UI logic)
  waitUntilSocketConnected(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (this.isSocketConnected()) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }
}
